import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { ThemeProvider } from '@/app/theme'
import { createQueryClient } from '@/app/queryClient'
import { ToastProvider } from '@/ui'
import { setUnauthenticatedHandler } from '@/api/client'
import { authKeys } from '@/features/auth/auth.queries'
import '@/i18n'
import '@/styles/theme.css'
import { routeTree } from './routeTree.gen'

const queryClient = createQueryClient()

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // The router already knows a preloaded query is fresh; re-checking on every hover would
  // undo the point of preloading.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/*
 * Any 401, from any request, ends the session here.
 *
 * The identity query is the single source of "am I signed in", so dropping it is enough — the
 * authenticated layout's guard sees no identity on its next run and redirects. Setting it to
 * `null` rather than invalidating avoids a refetch that is certain to 401 again.
 */
setUnauthenticatedHandler(() => {
  queryClient.setQueryData(authKeys.identity, null)
})

const container = document.getElementById('root')
if (!container) throw new Error('#root is missing from index.html')

createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)
